// src/pages/Platform/Tabs/Chatbot/ChatbotWidget/MessageDisplay.js

import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { InlineForm } from '../ChatbotBuilder/KnowledgeBase/Forms';
import LinkCarousel from './LinkCarousel';

// Helper to extract URLs from text
const extractUrls = (text) => {
  if (!text) return [];
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matches = [...text.matchAll(urlRegex)];
  const urls = matches.map(match => {
    let url = match[1];
    // Clean trailing punctuation common in markdown/text
    // e.g. [text](url) -> url)
    // text ended with url. -> url.

    // Iteratively strip trailing characters that are likely punctuation
    while (url.length > 0) {
      const lastChar = url.charAt(url.length - 1);
      if (/[.,;!?]/.test(lastChar)) {
        url = url.slice(0, -1);
      } else if (lastChar === ')') {
        // Count parens to handle valid parens in URLs vs markdown closing paren
        const open = (url.match(/\(/g) || []).length;
        const close = (url.match(/\)/g) || []).length;
        if (close > open) {
          url = url.slice(0, -1);
        } else {
          break;
        }
      } else if (lastChar === ']') {
        url = url.slice(0, -1);
      } else {
        break;
      }
    }
    return url;
  });

  return [...new Set(urls.filter(u => u.length > 10))]; // Valid URL min length check
};

// Format a timestamp as a relative "x minutes ago" label (Chatbase-style meta row)
const formatRelativeTime = (ts) => {
  if (!ts) return 'Just now';
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return 'Just now';
  const sec = Math.floor(Math.max(0, Date.now() - then) / 1000);
  if (sec < 45) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min} minute${min > 1 ? 's' : ''} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr > 1 ? 's' : ''} ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day > 1 ? 's' : ''} ago`;
  return new Date(ts).toLocaleDateString();
};

// Inline thumbs icon (no icon-lib dependency — safe for the embedded widget bundle).
// Thumbs-down is simply thumbs-up rotated 180°.
const ThumbIcon = ({ direction = 'up', filled = false }) => (
  <svg
    width="15" height="15" viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'} stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={direction === 'down' ? { transform: 'rotate(180deg)' } : undefined}
  >
    <path d="M7 10v12" />
    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
  </svg>
);

// Resolve the API base URL for widget → server calls (shared by preview + feedback)
const resolveApiBaseUrl = () => {
  let baseUrl = '';
  if (process.env.REACT_APP_API_BASE_URL) {
    baseUrl = process.env.REACT_APP_API_BASE_URL;
  } else if (process.env.REACT_APP_BASE_URL) {
    baseUrl = process.env.REACT_APP_BASE_URL;
  } else if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    baseUrl = 'http://localhost:5000';
  } else if (typeof window !== 'undefined') {
    baseUrl = window.location.origin;
  }
  return baseUrl.replace(/\/$/, '');
};

const MessageItem = ({
  msg,
  index,
  botMessageBoxColor,
  botMessageTextColor,
  userMessageBoxColor,
  userMessageTextColor,
  handleFormSubmit,
  showLinkPreviews = true,
  chatbotId
}) => {
  const [links, setLinks] = useState([]);
  const [feedback, setFeedback] = useState(null); // null | 'up' | 'down'
  const scrapedUrls = React.useRef(new Set());

  useEffect(() => {
    // Only fetch links for assistant messages that aren't streaming
    // Skip entirely when link previews are disabled for this chatbot
    if (msg.role !== 'assistant' || !msg.content || msg.isStreaming || !showLinkPreviews) return;

    const urls = extractUrls(msg.content);

    const baseUrl = resolveApiBaseUrl();

    // Filter out URLs we've already processed or started processing
    const newUrls = urls.filter(url => !scrapedUrls.current.has(url));

    if (newUrls.length > 0) {
      // Mark as scraping/scraped
      newUrls.forEach(url => scrapedUrls.current.add(url));

      newUrls.forEach(async (url) => {
        // Check if metadata already exists in the message object (from DB persistence)
        if (msg.link_metadata && msg.link_metadata[url]) {
          setLinks(prev => [...prev, msg.link_metadata[url]]);
          return;
        }

        // Otherwise, fetch from scraper (which will now save to DB)
        try {
          const payload = { url };
          if (msg.id) { // Pass messageId if available to save result to DB
            payload.messageId = msg.id;
          }

          const response = await axios.post(`${baseUrl}/api/scraper/preview`, payload);
          if (response.data) {
            setLinks(prev => [...prev, response.data]);
          }
        } catch (error) {
          console.error(`Error fetching link preview for ${url} at ${baseUrl}/api/scraper/preview:`, error);
          if (error.response && error.response.data) {
            setLinks(prev => [...prev, error.response.data]);
          }
        }
      });
    }
  }, [msg.content, msg.role, msg.link_metadata, msg.id, msg.isStreaming, showLinkPreviews]);

  // Keep local feedback in sync with the persisted value (present once the thread re-fetches)
  useEffect(() => {
    setFeedback(msg.feedback === 1 ? 'up' : msg.feedback === -1 ? 'down' : null);
  }, [msg.feedback]);

  // Record 👍 / 👎 — optimistic local update, then persist to the server
  const submitFeedback = (value) => {
    const next = feedback === value ? null : value; // clicking the active one clears it
    setFeedback(next);
    if (!msg.id || !chatbotId) return; // no DB row yet (e.g. mid-stream) → local only
    try {
      fetch(`${resolveApiBaseUrl()}/api/messages/${msg.id}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback: next, chatbotId })
      }).catch(() => { /* non-critical */ });
    } catch { /* non-critical */ }
  };

  // Handle form messages
  if (msg.role === 'form') {
    return (
      <InlineForm
        key={index}
        form={msg.formData}
        onSubmit={handleFormSubmit}
        botMessageBoxColor={botMessageBoxColor}
        botMessageTextColor={botMessageTextColor}
        userMessageBoxColor={userMessageBoxColor}
        userMessageTextColor={userMessageTextColor}
      />
    );
  }

  // Handle regular messages — Chatbase-style: bubble + (for assistant) a meta row below
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
        width: '100%'
      }}
    >
    <div
      className={`chatbot-message ${msg.role}`}
      style={{
        backgroundColor: msg.role === 'user' ? userMessageBoxColor : botMessageBoxColor,
        color: msg.role === 'user' ? userMessageTextColor : botMessageTextColor,
        // Add padding bottom if we have links to separate them visually from bubble edge
        // But links are outside the text bubble usually in WhatsApp? 
        // Actually, WhatsApp puts them inside/attached.
        // Let's render them inside the bubble for now, at the bottom.
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {msg.role === 'assistant' ? (
        <div style={{ color: botMessageTextColor, width: '100%' }} className={`markdown${msg.isStreaming && msg.content ? ' chatbot-streaming' : ''}`}>
          {/* Show streaming indicator for empty streaming messages */}
          {msg.isStreaming && msg.content === '' ? (
            <div className="streaming-indicator">
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : msg.content.includes('<') && msg.content.includes('>') ? (
            <div
              style={{ color: botMessageTextColor }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(msg.content, {
                  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'blockquote', 'code', 'pre'],
                  ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
                })
              }}
            />
          ) : (
            <ReactMarkdown
              components={{
                // Simple headings with standard spacing
                h1: ({ node, children, ...props }) => (
                  <h1 style={{ color: botMessageTextColor, fontWeight: 'bold' }} {...props}>
                    {children}
                  </h1>
                ),
                h2: ({ node, children, ...props }) => (
                  <h2 style={{ color: botMessageTextColor, fontWeight: 'bold' }} {...props}>
                    {children}
                  </h2>
                ),
                h3: ({ node, children, ...props }) => (
                  <h3 style={{ color: botMessageTextColor, fontWeight: 'bold' }} {...props}>
                    {children}
                  </h3>
                ),
                // Standard paragraphs
                p: ({ node, children, ...props }) => (
                  <p style={{ color: botMessageTextColor }} {...props}>
                    {children}
                  </p>
                ),
                // Standard lists
                ul: ({ node, ...props }) => <ul style={{ paddingLeft: '20px' }} {...props} />,
                ol: ({ node, ...props }) => <ol style={{ paddingLeft: '20px' }} {...props} />,
                li: ({ node, ...props }) => <li style={{ color: botMessageTextColor }} {...props} />,
                // Standard links
                a: ({ node, children, ...props }) => (
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: botMessageTextColor, textDecoration: 'underline' }}
                  >
                    {children}
                  </a>
                ),
                // Standard code
                code: ({ node, inline, className, children, ...props }) =>
                  inline ? (
                    <code style={{ backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '3px' }} {...props}>
                      {children}
                    </code>
                  ) : (
                    <pre style={{ backgroundColor: 'rgba(0,0,0,0.05)', padding: '10px', borderRadius: '5px', overflowX: 'auto' }}>
                      <code {...props}>
                        {children}
                      </code>
                    </pre>
                  ),
                // Standard blockquotes
                blockquote: ({ node, children, ...props }) => (
                  <blockquote style={{ borderLeft: '4px solid rgba(0,0,0,0.1)', paddingLeft: '16px', fontStyle: 'italic' }} {...props}>
                    {children}
                  </blockquote>
                ),
              }}
            >
              {msg.content}
            </ReactMarkdown>
          )}
          {/* Show streaming cursor if message is currently streaming and has content */}
          {msg.isStreaming && msg.content && (
            <span className="streaming-cursor">|</span>
          )}

          {/* Render Rich Link Previews */}
          {showLinkPreviews && !msg.isStreaming && links.length > 0 && (
            <div style={{ marginTop: '8px', borderTop: '1px solid rgba(0,0,0,0.05)', paddingTop: '8px' }}>
              <LinkCarousel links={links} />
            </div>
          )}
        </div>
      ) : (
        <div style={{ color: userMessageTextColor }}>{msg.content}</div>
      )}
    </div>

    {/* Meta row (relative time + feedback) — assistant messages only, Chatbase-style */}
    {msg.role === 'assistant' && !msg.isStreaming && msg.content && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 6px 14px', fontSize: '12px' }}>
        <span style={{ color: botMessageTextColor, opacity: 0.5 }}>{formatRelativeTime(msg.timestamp)}</span>
        <span style={{ color: botMessageTextColor, opacity: 0.22 }}>|</span>
        <button
          type="button"
          aria-label="Helpful"
          onClick={() => submitFeedback('up')}
          style={{ background: 'none', border: 'none', padding: '2px', cursor: 'pointer', display: 'inline-flex', color: botMessageTextColor, opacity: feedback === 'up' ? 1 : 0.5, transition: 'opacity 0.15s' }}
        >
          <ThumbIcon direction="up" filled={feedback === 'up'} />
        </button>
        <button
          type="button"
          aria-label="Not helpful"
          onClick={() => submitFeedback('down')}
          style={{ background: 'none', border: 'none', padding: '2px', cursor: 'pointer', display: 'inline-flex', color: botMessageTextColor, opacity: feedback === 'down' ? 1 : 0.5, transition: 'opacity 0.15s' }}
        >
          <ThumbIcon direction="down" filled={feedback === 'down'} />
        </button>
      </div>
    )}
    </div>
  );
};

const MessageDisplay = ({
  messages,
  botMessageBoxColor,
  botMessageTextColor,
  userMessageBoxColor,
  userMessageTextColor,
  handleFormSubmit,
  showLinkPreviews = true,
  chatbotId
}) => {
  return (
    <>
      {messages.map((msg, index) => (
        <MessageItem
          key={msg.clientId || msg.id || msg.timestamp || `msg-${index}`}
          index={index}
          msg={msg}
          botMessageBoxColor={botMessageBoxColor}
          botMessageTextColor={botMessageTextColor}
          userMessageBoxColor={userMessageBoxColor}
          userMessageTextColor={userMessageTextColor}
          handleFormSubmit={handleFormSubmit}
          showLinkPreviews={showLinkPreviews}
          chatbotId={chatbotId}
        />
      ))}
    </>
  );
};

export default MessageDisplay; 